import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { crefisaService } from "@/app/services/crefisaService";
import { currentStep } from "@/app/store";
import { BasicInput } from "@/view/components/BasicInput";
import { Button } from "@/view/components/ui/button";
import { useToast } from "@/view/components/ui/use-toast";
import { useMutation } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

interface BankInfoFormProps {
  visible?: boolean;
  onContinue: () => void;
}

const FIXED_BANK = "104";
const FIXED_AGENCY = "38800";
const FIXED_ACCOUNT_TYPE = "CP";

export const bankFormSchema = z.object({
  bank: z.string({ required_error: "* obrigatório" }).min(1, "* obrigatório"),
  agency: z
    .string()
    .min(4, "* obrigatório")
    .max(5, "No máximo 5 caracteres"),
  account: z
    .string()
    .min(4, "* obrigatório")
    .max(11, "No máximo 11 caracteres"),
});

const onlyDigitsInput = (e: React.FormEvent<HTMLInputElement>) => {
  const target = e.currentTarget;
  target.value = target.value.replace(/\D/g, "");
};

export default function BankInfoForm({
  visible,
  onContinue,
}: BankInfoFormProps) {
  const [, setStep] = useAtom(currentStep);
  const { customerData, simulationData, setCustomer } = useCrefisaContext();

  const { toast } = useToast();

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: any) => {
      return crefisaService.createClient(simulationData!.clientId!, data);
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const agencyValue = String(data.bankAccount?.agency ?? "");
      const accountValue = String(data.bankAccount?.account ?? "");

      const bankAccount = {
        bank: data.bankAccount?.bank ?? FIXED_BANK,
        accountType: FIXED_ACCOUNT_TYPE,
        agency: agencyValue.slice(0, -1),
        agencyDigit: agencyValue.slice(-1),
        account: accountValue.slice(0, -1),
        accountDigit: accountValue.slice(-1),
      };

      const payload = {
        ...data.personalInfo,
        hasVisualImpairment: false,
        address: data.address,
        bankAccount,
        income: customerData?.income,
      };

      const response = await mutateAsync(payload);

      if (!response.data.status) {
        toast({
          variant: "destructive",
          title: "Erro ao criar cliente",
          description:
            response?.data?.mensagem ||
            "Erro inesperado. Tente novamente mais tarde.",
        });
        return;
      }

      setCustomer({
        ...payload,
        customerId: response!.customerId,
      });
      onContinue();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao criar cliente",
        description:
          error?.message || "Erro inesperado. Tente novamente mais tarde.",
      });
    }
  };

  useEffect(() => {
    setValue("bankAccount.bank", FIXED_BANK);
    setValue("bankAccount.agency", FIXED_AGENCY);
  }, [setValue]);

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <div className="grid grid-cols-inputs gap-y-4">
        <BasicInput
          name="bankAccount.bank"
          label="Banco"
          error={(errors.bankAccount as any)?.bank?.message}
          disabled
        />
      </div>
      <div className="mr-8 grid grid-cols-2 gap-4">
        <BasicInput
          tabIndex={0}
          name="bankAccount.agency"
          label="Agência com dígito"
          error={(errors.bankAccount as any)?.agency?.message}
          inputMode="numeric"
          maxLength={5}
          disabled
        />
        <BasicInput
          tabIndex={0}
          name="bankAccount.account"
          label="Conta com dígito"
          error={(errors.bankAccount as any)?.account?.message}
          inputMode="numeric"
          maxLength={11}
          onInput={onlyDigitsInput}
        />
      </div>
      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          type="submit"
          isLoading={isPending}
          onClick={handleSubmit(onSubmit)}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
